import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  SparklesIcon,
  AcademicCapIcon,
  LightBulbIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Article, articleAPI } from '../services/api';
import HeroImage from './HeroImage';

interface ArticleDetailModalProps {
  article: Article;
  ageGroup: '6-9' | '10-13' | '14+';
  onClose: () => void;
}

const ArticleDetailModal: React.FC<ArticleDetailModalProps> = ({ article, ageGroup, onClose }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeBusy, setLikeBusy] = useState(false);

  const articleId = typeof article.id === 'string' ? parseInt(article.id, 10) : article.id;

  // On open: record the read (every open counts) and load like status.
  useEffect(() => {
    if (!articleId || isNaN(articleId)) return;
    articleAPI.markRead(articleId, ageGroup).catch((e) => console.error('markRead failed:', e));
    articleAPI
      .getLikeStatus(articleId, ageGroup)
      .then((s) => {
        setIsLiked(s.liked);
        setLikeCount(s.like_count);
      })
      .catch((e) => console.error('getLikeStatus failed:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId, ageGroup]);

  const handleToggleLike = async () => {
    if (!articleId || isNaN(articleId) || likeBusy) return;
    setLikeBusy(true);
    // optimistic update
    setIsLiked((v) => !v);
    setLikeCount((c) => c + (isLiked ? -1 : 1));
    try {
      const res = await articleAPI.toggleLike(articleId, ageGroup);
      setIsLiked(res.liked);
      setLikeCount(res.like_count);
    } catch (e) {
      console.error('toggleLike failed:', e);
      // revert on failure
      setIsLiked((v) => !v);
      setLikeCount((c) => c + (isLiked ? 1 : -1));
    } finally {
      setLikeBusy(false);
    }
  };

  const getModalStyle = () => {
    if (ageGroup === '6-9') {
      return 'bg-gradient-to-br from-yellow-50 to-orange-50';
    } else if (ageGroup === '10-13') {
      return 'bg-white';
    }
    return 'bg-gray-50';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className={`${getModalStyle()} rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border-2 border-gray-100 flex flex-col`}
      >
        {/* Header */}
        <div className={`
          flex-none
          p-4 sm:p-6 border-b
          ${ageGroup === '6-9' ? 'bg-gradient-to-r from-yellow-300 to-orange-300' :
            ageGroup === '10-13' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
            'bg-gradient-to-r from-indigo-600 to-purple-600'}
          text-white
        `}>
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2 flex-wrap">
                <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                  {article.category.replace('_', ' ')}
                </span>
                {article.emoji_summary && (
                  <span className="text-base sm:text-lg">{article.emoji_summary}</span>
                )}
              </div>
              <h2 className={`
                font-bold mb-2
                ${ageGroup === '6-9' ? 'text-2xl sm:text-3xl font-kid' : 'text-xl sm:text-2xl'}
              `}>
                {article.title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="bg-white/20 backdrop-blur-md p-2 rounded-xl hover:bg-white/40 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl flex-shrink-0"
            >
              <XMarkIcon className="h-6 w-6 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>

        {/* Content. flex-1 + min-h-0 lets this fill whatever the header
            leaves, at any header height. A fixed calc(90vh - 200px) assumed
            a 200px header, so on phones (where the title wraps to two or
            three lines) the tail of the article — including the like button
            — fell outside the modal and could not be scrolled to. */}
        <div className="bg-white overflow-y-auto p-4 sm:p-6 flex-1 min-h-0">
                {/* Hero image (omitted entirely when there's no image) */}
                <HeroImage
                  url={article.hero_image_url}
                  alt={article.title}
                  variant="hero"
                  className="mb-6 rounded-xl"
                />

                {/* Main Article Content First - Most Prominent */}
                <div className={`
                  mb-6 sm:mb-8
                  ${ageGroup === '6-9' ? 'text-base sm:text-lg leading-relaxed' : 'text-sm sm:text-base leading-relaxed'}
                `}>
                  <p className="whitespace-pre-wrap text-gray-800">{article.content}</p>
                </div>

                {/* Separator */}
                <hr className="my-8 border-t-2 border-gray-200" />

                {/* Supporting Content Section - Less Prominent */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Learn More</h3>

                  {/* Engagement Features - 3 Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {article.did_you_know && (
                      <div className="bg-gray-50 border border-gray-200 p-3 rounded">
                        <div className="flex items-center mb-1">
                          <SparklesIcon className="h-4 w-4 text-gray-500 mr-1" />
                          <span className="text-xs font-semibold text-gray-600 uppercase">Did You Know?</span>
                        </div>
                        <p className="text-xs text-gray-600">{article.did_you_know}</p>
                      </div>
                    )}

                    {article.real_world_connection && (
                      <div className="bg-gray-50 border border-gray-200 p-3 rounded">
                        <div className="flex items-center mb-1">
                          <AcademicCapIcon className="h-4 w-4 text-gray-500 mr-1" />
                          <span className="text-xs font-semibold text-gray-600 uppercase">Real World Connection</span>
                        </div>
                        <p className="text-xs text-gray-600">{article.real_world_connection}</p>
                      </div>
                    )}

                    {article.picture_this && (
                      <div className="bg-gray-50 border border-gray-200 p-3 rounded">
                        <div className="flex items-center mb-1">
                          <LightBulbIcon className="h-4 w-4 text-gray-500 mr-1" />
                          <span className="text-xs font-semibold text-gray-600 uppercase">Picture This</span>
                        </div>
                        <p className="text-xs text-gray-600">{article.picture_this}</p>
                      </div>
                    )}
                  </div>

                </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t-2 border-gray-100 bg-white p-4 md:p-4 shadow-inner">
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0">
            <button
              onClick={handleToggleLike}
              disabled={likeBusy}
              className="flex items-center justify-center space-x-2 text-gray-600 hover:text-red-500 transition-all duration-200 active:scale-95 font-medium py-2.5 sm:py-0 disabled:opacity-60"
            >
              {isLiked ? (
                <HeartSolidIcon className="h-6 w-6 text-red-500" />
              ) : (
                <HeartIcon className="h-6 w-6" />
              )}
              <span className="text-base">{isLiked ? 'Liked' : 'Like'}{likeCount > 0 ? ` · ${likeCount}` : ''}</span>
            </button>
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2.5 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 active:scale-95 transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ArticleDetailModal;